import { UsageAtLoss } from '../../intake';
import { toCalendarDay } from './evaluate';
import { Finding, Model } from './model';
import { Outcome } from './outcome';

/** The claim and job values an evaluation depends on, as they are now. */
export interface CurrentInputs {
  lossDate: Date | string | null;
  claimDate: Date | string | null;
  coverageId: string | null;
  usageAtLoss: UsageAtLoss | null;
  submissionId: string | null;

  /** Cents across the job's charges; left out, it isn't compared. */
  requestedAmount?: number;
}

export type ChangedInput = keyof CurrentInputs;

/** Outcomes that stop an approval. */
export const BLOCKING_OUTCOMES: Outcome[] = [Outcome.INELIGIBLE, Outcome.EXCLUDED, Outcome.INSUFFICIENT_INFORMATION];

export type ApprovalProblem =
  | { code: 'NOT_EVALUATED' }
  | { code: 'OUT_OF_DATE'; changed: ChangedInput[] }
  | { code: 'NOT_ELIGIBLE'; outcome: Outcome; finding: Finding | null }
  | { code: 'OVER_LIMIT'; limit: number; authorized: number };

function dayOf(value: Date | string | null, timeZone: string): string | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : toCalendarDay(date, timeZone);
}

/** What changed on the claim or job since this evaluation ran. */
export function changedInputs(result: Pick<Model, 'inputs'>, current: CurrentInputs): ChangedInput[] {
  const { inputs } = result;
  const changed: ChangedInput[] = [];

  if (dayOf(current.lossDate, inputs.timeZone) !== inputs.lossDate) changed.push('lossDate');
  if (dayOf(current.claimDate, inputs.timeZone) !== inputs.claimDate) changed.push('claimDate');
  if ((current.coverageId ?? null) !== inputs.coverageId) changed.push('coverageId');
  if ((current.submissionId ?? null) !== inputs.submissionId) changed.push('submissionId');

  const before = inputs.usageAtLoss;
  const now = current.usageAtLoss ?? null;
  if (before?.dimensionId !== now?.dimensionId || before?.value !== now?.value) changed.push('usageAtLoss');

  const requested = current.requestedAmount;
  if (requested !== undefined && requested !== inputs.requestedAmount) changed.push('requestedAmount');

  return changed;
}

/** Why the latest result blocks this approval, or null. `authorizedAmount` is null for a job without charges. */
export function approvalProblem(
  latest: Pick<Model, 'outcome' | 'findings' | 'limits' | 'inputs'> | null,
  current: CurrentInputs,
  authorizedAmount: number | null
): ApprovalProblem | null {
  if (!latest) return { code: 'NOT_EVALUATED' };

  const changed = changedInputs(latest, current);
  if (changed.length) return { code: 'OUT_OF_DATE', changed };

  if (BLOCKING_OUTCOMES.includes(latest.outcome)) {
    return { code: 'NOT_ELIGIBLE', outcome: latest.outcome, finding: latest.findings[0] ?? null };
  }

  if (latest.limits && authorizedAmount !== null && authorizedAmount > latest.limits.remainingLimit) {
    return { code: 'OVER_LIMIT', limit: latest.limits.remainingLimit, authorized: authorizedAmount };
  }

  return null;
}
